import FieldError from "../../Common/EditForm/FieldError";
import Label from "../../Common/EditForm/Label";
import { appendValidOrInvalidClassName } from "../../Common/EditForm/appendErrorClassNames";
import { forwardRef, useId, useImperativeHandle, useState } from "react";
import { Form } from "react-bootstrap";
import {
  type Control,
  type FieldValues,
  type UseControllerProps,
  useController,
} from "react-hook-form";
import BusinessDocumentItemSelectorDialog from "./BusinessDocumentItemSelectorDialog";
import type businessDocumentItemVM from "./businessDocumentItemVM";

/** Methods exposed by the selector. */
export type BusinessDocumentItemSelectorFragmentV2Handle = {
  openSelector: () => void;
};

/** Properties exposed by the selector that doesn't belong to the RFC infrastructure. */
interface BusinessDocumentItemSelectorFragmentV2OwnProps {
  required?: boolean;
  onItemChanged?: (item: businessDocumentItemVM) => void;
  businessDocumentType: number;
  documentNumber: string;
}

/** Combined own properties and the properties required by the RFC infrastructure. */
export type BusinessDocumentItemSelectorFragmentV2Props<T extends FieldValues> =
    BusinessDocumentItemSelectorFragmentV2OwnProps &
    UseControllerProps<T> & { control: Control<T> }; //  UseControllerProps<T> already contains the control property, but here we require control to be passed directly (the control is explicitly required now)

/** The actual component, but it is actually wrapped by forwardRef so here is the inner part. */
function BusinessDocumentItemSelectorFragmentV2Inner<T extends FieldValues>(
  props: BusinessDocumentItemSelectorFragmentV2Props<T>,
  ref: React.ForwardedRef<BusinessDocumentItemSelectorFragmentV2Handle>,
) {
  const localProps = { ...props };

  localProps.rules = { ...props.rules };
  localProps.rules.validate = { ...props.rules?.validate };

  if (props.required) {
    // we intentionally don't use rules.required, as it has its strong typing
    localProps.rules.validate.valueRequired = (
      value: businessDocumentItemVM | null,
    ): string | undefined =>
      value !== null ? undefined : "Pole jest wymagane.";
  }

  const { field, fieldState } = useController(localProps);

  const isTouched = fieldState.isTouched;
  const error = fieldState.error;

  const [isSelectorShown, setSelectorShown] = useState<boolean>(false);

  const lineCodeId = useId();
  const catalogNumberId = useId();

  const onShowSelector = () => {
    setSelectorShown(true);
  };

  // Expose openSelector() to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      openSelector: () => setSelectorShown(true),
    }),
    [],
  );

  const onSelectorOk = (businessDocumentItem: businessDocumentItemVM) => {
    setSelectorShown(false);
    field.onChange(businessDocumentItem);
    props.onItemChanged?.(businessDocumentItem);
  };

  return (
    <>
      <div className="row mb-3">
        <div className="col-sm-8 offset-sm-4">
          <div className="row">
            <div className="col-sm-12 col-xxl-8 col-xl-10">
              <button
                className="btn btn-success w-100"
                type="button"
                title="Wybierz pozycję z dokumentu"
                onClick={() => onShowSelector()}
              >
                <i className="bi bi-hand-index ps-2 me-2"></i> Wybierz z listy
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="row mb-3">
        <Label
          label="Kod pozycji / numer katalogowy *"
          id={lineCodeId}
          isError={!!error && !!isTouched}
          description="Kod pozycji z dokumentu / numer katalogowy towaru. Zostaną uzupełnione automatycznie z wybranej pozycji dokumentu."
        ></Label>

        <div className="col-sm-8">
          <div className="row">
            <div className="col-sm-6 col-xxl-4 col-xl-5">
              <div className="form-floating">
                <input
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  readOnly={true}
                  aria-disabled="true"
                  onClick={() => onShowSelector()}
                  id={lineCodeId}
                  className={appendValidOrInvalidClassName(
                    "form-control -app-fake-disabled",
                    error,
                    isTouched,
                  )}
                  type="text"
                  value={field.value?.lineCode || ""}
                  name="lineCode"
                  placeholder="Kod pozycji"
                />
                <label htmlFor="lineCode">Kod pozycji</label>
              </div>
              <FieldError
                error={error?.message as string | null | undefined}
              ></FieldError>
            </div>

            <div className="col-sm-6 col-xxl-4 col-xl-5">
              <div className="form-floating">
                <input
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  readOnly={true}
                  aria-disabled="true"
                  onClick={() => onShowSelector()}
                  id={catalogNumberId}
                  className={appendValidOrInvalidClassName(
                    "form-control -app-fake-disabled",
                    error,
                    isTouched,
                  )}
                  type="text"
                  value={field.value?.catalogNumber || ""}
                  name="catalogNumber"
                  placeholder="Numer katalogowy"
                />
                <label htmlFor="catalogNumber">Numer katalogowy</label>
              </div>
              <FieldError
                error={error?.message as string | null | undefined}
              ></FieldError>
            </div>
            <small>Kliknij przycisk lub jedno z pól, aby otworzyć listę pozycji.</small>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <Label
          label="Nazwa towaru *"
          id={field.name}
          isError={!!error && !!isTouched}
          description="Nazwa towaru. Zostanie uzupełniona automatycznie z wybranej pozycji dokumentu."
        ></Label>

        <div className="col-sm-8">
          <div className="row">
            <div className="col-sm-12 col-xxl-8 col-xl-10">
              <Form.Control
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                readOnly={true}
                aria-disabled="true"
                onClick={() => onShowSelector()}
                id={field.name}
                className={appendValidOrInvalidClassName(
                  "-app-fake-disabled",
                  error,
                  isTouched,
                )}
                onBlur={field.onBlur}
                value={field.value?.productName || ""}
                name={field.name}
                ref={field.ref}
                placeholder="Uzupełni się automatycznie po wyborze pozycji"
              />
              <FieldError
                error={error?.message as string | null | undefined}
              ></FieldError>
            </div>
          </div>
        </div>
      </div>

      <BusinessDocumentItemSelectorDialog
        show={isSelectorShown}
        businessDocumentType={props.businessDocumentType}
        documentNumber={props.documentNumber}
        ok={onSelectorOk}
        cancel={() => {
          setSelectorShown(false);
        }}
      ></BusinessDocumentItemSelectorDialog>
    </>
  );
}

// Wrap with forwardRef, then cast to keep generics on the exported component
export const BusinessDocumentItemSelectorV2Fragment = forwardRef(
  BusinessDocumentItemSelectorFragmentV2Inner,
) as <T extends FieldValues>(
  props: BusinessDocumentItemSelectorFragmentV2Props<T> &
    React.RefAttributes<BusinessDocumentItemSelectorFragmentV2Handle>,
) => React.ReactElement;

export default BusinessDocumentItemSelectorV2Fragment;
